import { useEffect, useRef } from "react";
import styles from "./LumiAiAssistant.module.scss";
import useAiChatSession from "../../../hooks/useAiChatSession";
import AiCreditBalance from "./AiCreditBalance";
import AssistantMarkdown from "../../../components/secondaryComponents/AssistantMarkdown";
import useFillViewportHeight from "./useFillViewportHeight";
import AiUsageMeter from "./AiUsageMeter";
import ConversationRow from "./ConversationRow";
import InlineTitleEditor from "./InlineTitleEditor";
import InteractivePrompt from "./InteractivePrompt";
import { AI_SUGGESTED_PROMPTS } from "../../../helpers/constants";
import { formatCurrency, formatMessageTime } from "../../../helpers";
import { TemiSparkIcon } from "../../../assets/svgIcons";

const LumiAiAssistant = () => {
  const {
    messageInput,
    setMessageInput,
    localMessages,
    isStreaming,
    isSidebarOpen,
    isSending,
    isLoadingConversations,
    isLoadingDetail,
    conversationList,
    activeConversationId,
    activeConversationTitle,
    copiedMessageIndex,
    unitBalance,
    isLoadingUnitBalance,
    isUnitBalanceError,
    usage,
    showLimitWarning,
    dismissLimitWarning,
    isAiBlocked,
    blockedReason,
    canTopUpForBlock,
    renamingId,
    renameDraft,
    updateRenameDraft,
    startRename,
    cancelRename,
    commitRename,
    maxTitleLength,
    senderName,
    messagesEndRef,
    inputRef,
    handleSendMessage,
    handleSuggestedPrompt,
    handleRegenerateMessage,
    handleKeyDown,
    handleNewChat,
    handleSelectConversation,
    handleDeleteConversation,
    handleTopUpCredit,
    handleCopyMessage,
    toggleSidebar,
  } = useAiChatSession();
  const shellRef = useFillViewportHeight();
  const titleLongPressRef = useRef(null);

  const isBusy = isSending || isStreaming;

  // Long-press is the touch equivalent of the double-click to rename
  const handleTitleTouchStart = () => {
    if (!activeConversationId) return;
    clearTimeout(titleLongPressRef.current);
    titleLongPressRef.current = setTimeout(
      () => startRename(activeConversationId, activeConversationTitle),
      500,
    );
  };
  const handleTitleTouchEnd = () => clearTimeout(titleLongPressRef.current);

  useEffect(() => () => clearTimeout(titleLongPressRef.current), []);

  return (
    <div className={styles.aiChat} ref={shellRef}>
      {/* History panel */}
      <aside
        className={`${styles.aiChat__history} ${
          !isSidebarOpen ? styles["aiChat__history--collapsed"] : ""
        }`}
      >
        <div className={styles.aiChat__historyHead}>
          <h2>Chat history</h2>
          {/* The panel overlays the topbar toggle on smaller screens */}
          <button
            className={`${styles.aiChat__iconBtn} ${styles.aiChat__historyClose}`}
            onClick={toggleSidebar}
            title="Close chat history"
            aria-label="Close chat history"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <button className={styles.aiChat__newChat} onClick={handleNewChat}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          New chat
        </button>

        <div className={styles.aiChat__historyList}>
          {isLoadingConversations ? (
            <div className={styles.aiChat__historyEmpty}>
              Loading conversations...
            </div>
          ) : conversationList.length === 0 ? (
            <div className={styles.aiChat__historyEmpty}>
              No conversations yet. Start a new chat.
            </div>
          ) : (
            conversationList.map((convo) => (
              <ConversationRow
                key={convo.id}
                conversation={convo}
                isActive={activeConversationId === convo.id}
                isRenaming={renamingId === convo.id}
                renameDraft={renameDraft}
                onRenameDraftChange={updateRenameDraft}
                onCommitRename={commitRename}
                onCancelRename={cancelRename}
                onStartRename={startRename}
                maxTitleLength={maxTitleLength}
                onSelect={handleSelectConversation}
                onDelete={handleDeleteConversation}
              />
            ))
          )}
        </div>
      </aside>

      {isSidebarOpen && (
        <div
          className={styles.aiChat__historyBackdrop}
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Chat area */}
      <main className={styles.aiChat__main}>
        <div className={styles.aiChat__topbar}>
          <button
            className={styles.aiChat__iconBtn}
            onClick={toggleSidebar}
            title="Toggle chat history"
            aria-label="Toggle chat history"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect
                x="1"
                y="2"
                width="14"
                height="12"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path d="M6 2V14" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </button>
          {activeConversationId && renamingId === activeConversationId ? (
            <InlineTitleEditor
              value={renameDraft}
              onChange={updateRenameDraft}
              onCommit={commitRename}
              onCancel={cancelRename}
              maxLength={maxTitleLength}
              className={styles.aiChat__titleEditor}
            />
          ) : (
            <span
              className={`${styles.aiChat__title} ${
                activeConversationId ? styles["aiChat__title--editable"] : ""
              }`}
              onDoubleClick={() =>
                activeConversationId &&
                startRename(activeConversationId, activeConversationTitle)
              }
              onTouchStart={handleTitleTouchStart}
              onTouchEnd={handleTitleTouchEnd}
              onTouchMove={handleTitleTouchEnd}
              title={
                activeConversationId ? "Double-click to rename" : "Ask Temi"
              }
            >
              {activeConversationId
                ? activeConversationTitle || "Untitled chat"
                : "Ask Temi"}
            </span>
          )}
          <span className={styles.aiChat__badge}>Beta</span>

          <div className={styles.aiChat__topbarMeta}>
            <AiUsageMeter usage={usage} />

            <AiCreditBalance
              balance={unitBalance}
              // Once a reply has landed the chips come from its usage block, so
              // a failed balance call is no reason to hide them.
              isLoading={isLoadingUnitBalance && !usage}
              isError={isUnitBalanceError && !usage}
              onTopUp={handleTopUpCredit}
            />
          </div>
        </div>

        <div className={styles.aiChat__scroll}>
          <div className={styles.aiChat__thread}>
            {isLoadingDetail ? (
              <div className={styles.aiChat__welcome}>
                <p>Loading conversation...</p>
              </div>
            ) : localMessages.length === 0 ? (
              <div className={styles.aiChat__welcome}>
                <div className={styles.aiChat__welcomeIcon}>
                  <TemiSparkIcon width="34" height="34" />
                </div>
                <h4>Hi, I&apos;m Temi</h4>
                <p>
                  Ask me anything about your business: orders, products,
                  inventory, sales and customers.
                </p>

                <div className={styles.aiChat__prompts}>
                  {AI_SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      className={styles.aiChat__prompt}
                      onClick={() => handleSuggestedPrompt(prompt)}
                      disabled={isBusy || isAiBlocked}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              localMessages.map((msg, idx) => {
                const time = formatMessageTime(msg.at);
                const isSettled =
                  !msg.isLoading && !msg.isStreaming && !!msg.content;

                if (msg.role === "user")
                  return (
                    <div key={idx} className={styles["aiChat__turn--user"]}>
                      <div className={styles.aiChat__meta}>
                        <span className={styles.aiChat__metaName}>
                          {senderName}
                        </span>
                        {time && (
                          <span className={styles.aiChat__metaTime}>
                            {time}
                          </span>
                        )}
                      </div>
                      <div className={styles.aiChat__bubble}>{msg.content}</div>
                      {isSettled && (
                        <button
                          className={styles.aiChat__copyPlain}
                          onClick={() => handleCopyMessage(msg.content, idx)}
                          aria-label="Copy message"
                        >
                          {copiedMessageIndex === idx ? "Copied" : "Copy"}
                        </button>
                      )}
                    </div>
                  );

                return (
                  <div key={idx} className={styles["aiChat__turn--assistant"]}>
                    <div className={styles.aiChat__avatar}>
                      <TemiSparkIcon width="18" height="18" />
                    </div>
                    <div
                      className={`${styles.aiChat__aiBody} ${
                        msg.isError ? styles["aiChat__aiBody--error"] : ""
                      }`}
                    >
                      <div className={styles.aiChat__meta}>
                        <span className={styles.aiChat__metaName}>Temi</span>
                        {time && (
                          <span className={styles.aiChat__metaTime}>
                            {time}
                          </span>
                        )}
                      </div>

                      {msg.isLoading ? (
                        <div className={styles.aiChat__loading}>
                          <span />
                          <span />
                          <span />
                        </div>
                      ) : (
                        <div className={styles.aiChat__aiContent}>
                          <AssistantMarkdown content={msg.content} />
                          {msg.isStreaming && (
                            <span className={styles.aiChat__cursor} />
                          )}
                        </div>
                      )}

                      {/* Only the latest turn can still be answered — once a
                          new message follows it, the picker it asked with is stale. */}
                      {isSettled &&
                        msg.interactive_prompt &&
                        idx === localMessages.length - 1 && (
                          <InteractivePrompt
                            prompt={msg.interactive_prompt}
                            onAnswer={handleSuggestedPrompt}
                            disabled={isBusy || isAiBlocked}
                          />
                        )}

                      {isSettled && (
                        <div className={styles.aiChat__actions}>
                          <button
                            className={styles.aiChat__chip}
                            onClick={() => handleCopyMessage(msg.content, idx)}
                            aria-label="Copy answer"
                          >
                            {copiedMessageIndex === idx ? "Copied" : "Copy"}
                          </button>
                          <button
                            className={styles.aiChat__chip}
                            onClick={() => handleRegenerateMessage(idx)}
                            disabled={isBusy || isAiBlocked}
                          >
                            Regenerate
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className={styles.aiChat__composerWrap}>
          {/* Approaching the daily limit — dismissible for the session */}
          {showLimitWarning && (
            <div className={styles.aiChat__limitNote}>
              <p>
                You&apos;ve used {usage.percent_used}% of today&apos;s{" "}
                {formatCurrency({
                  value: Number(usage.daily_limit),
                  currencyCode: "NGN",
                })}{" "}
                AI limit. It resets at midnight.
              </p>
              <button
                onClick={dismissLimitWarning}
                title="Dismiss"
                aria-label="Dismiss limit warning"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Blocked: out of credit, or the daily cap is spent */}
          {isAiBlocked && (
            <div className={styles.aiChat__blockedBanner}>
              <p>{blockedReason}</p>
              {/* Topping up cannot lift a daily-limit block, so it is not offered */}
              {canTopUpForBlock && (
                <button
                  className={styles.aiChat__bannerAction}
                  onClick={handleTopUpCredit}
                >
                  Top up credit
                </button>
              )}
            </div>
          )}

          <div className={styles.aiChat__composer}>
            <textarea
              ref={inputRef}
              className={styles.aiChat__composerField}
              placeholder={
                isAiBlocked
                  ? canTopUpForBlock
                    ? "Top up your credit to continue chatting"
                    : "Today's AI limit is spent, it resets at midnight"
                  : "Message Temi…"
              }
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isBusy || isAiBlocked}
            />
            <button
              className={styles.aiChat__send}
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || isBusy || isAiBlocked}
              title="Send message"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M3 9H15M15 9L10 4M15 9L10 14"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <p className={styles.aiChat__disclaimer}>
            Temi can make mistakes. Double-check important information.
          </p>
        </div>
      </main>
    </div>
  );
};

export default LumiAiAssistant;
